import {LoginFormAction} from './LoginFormActions'
import {LoginForm, LoginFormInput, LoginFormValidation} from './LoginFormState'
import * as LoginResource from '../../Request/LoginResource'
import {LoginFormFeedback} from './LoginFormFeedback'
import * as Result from '../../Data/Result'
import {decodeHttpResponse, NetworkError, sendRequest} from '../../Data/Http'
import {update} from '../../Data/ObjectHelper'
import {
    emailValidator,
    mandatoryStringValidator,
    validateInputField
} from '../../Data/FormValidation'
import {FeedbackLoop} from '../../Data/FeedbackLoop'
import * as LoginData from '../../Entities/LoginData'
import * as LoginResponse from '../../Entities/LoginResponse'

type LoginFormLoop =
    FeedbackLoop<LoginForm, LoginFormFeedback>

export function initLoginForm(email: string): LoginForm {
    return {
        input: {
            email,
            password: '',
        },
        loading: false,
        submissionAttempted: false,
        validation: {
            email: null,
            password: null,
        },
        error: null,
    }
}

export function loginFormReducer(
    state: LoginForm,
    action: LoginFormAction
): LoginFormLoop {
    switch (action.type) {
        case 'EmailChanged':
            const
                {email} = action,
                emailValidation =
                    validateInputField(
                        validators.email,
                        state.submissionAttempted,
                        email)
            return {
                state: update({
                    input: update({
                        email,
                    }, state.input),
                    validation: update({
                        email: emailValidation,
                    }, state.validation),
                    error: resetError('EmailNotFound', state.error),
                }, state),
                feedback: [],
            }
        case 'PasswordChanged':
            const
                {password} = action,
                passwordValidation =
                    validateInputField(
                        validators.password,
                        state.submissionAttempted,
                        password)
            return {
                state: update({
                    input: update({
                        password,
                    }, state.input),
                    validation: update({
                        password: passwordValidation,
                    }, state.validation),
                    error: resetError('PasswordWrong', state.error),
                }, state),
                feedback: [],
            }
        case 'Submit':
            const validationOutdome = validate(state.input)
            return ((): LoginFormLoop => {
               switch (validationOutdome.type) {
                   case 'Ok': {
                       const mapAction = (
                           result: Result.Result<NetworkError, string>,
                       ): LoginFormAction => ({
                           type: 'ReceivedLoginResponse',
                           result,
                       })
                       return {
                           state: update({
                               loading: true,
                           }, state),
                           feedback: [{
                               type: 'RunEffect',
                               effect:
                                   sendRequest(
                                       mapAction,
                                       LoginResource.submit(
                                           validationOutdome.value,
                                       ),
                                   ),
                           }],
                       }
                   }
                   case 'Err':
                       return {
                           state: update({
                               submissionAttempted: true,
                           }, state),
                           feedback: [],
                       }
               }
            })()
        case 'ReceivedLoginResponse': {
            const result =
                decodeHttpResponse(
                    LoginResponse.decoder(),
                    action.result)
            return ((): LoginFormLoop => {
                switch (result.type) {
                    case 'Ok':
                        const response: LoginResponse.M = result.value
                        switch (response.type) {
                            case 'LoginSuccess':
                                return {
                                    state: update({
                                        loading: false,
                                        error: null,
                                    }, state),
                                    feedback: [{
                                        type: 'LoginSucceeded',
                                        intermediateSession:
                                        response.intermediateSession,
                                    }],
                                }
                            case 'EmailNotFound':
                                return {
                                    state: update({
                                        loading: false,
                                        error: 'EmailNotFound',
                                    }, state),
                                    feedback: [],
                                }
                            case 'PasswordWrong':
                                return {
                                    state: update({
                                        loading: false,
                                        error: 'PasswordWrong',
                                    }, state),
                                    feedback: [],
                                }
                            default:
                                return {state, feedback: []}
                        }
                    case 'Err':
                        return {
                            state: update({
                                loading: false,
                                error: 'GeneralError',
                            }, state),
                            feedback: [],
                        }
                }
            })()
        }
        case 'Logout':
            return {
                state,
                feedback: [{type: 'PerformLogout'}],
            }
    }
}

const validators = {
    email:
        (value: string) =>
            Result.andThen(
                emailValidator(),
                mandatoryStringValidator()(value)),
    password: mandatoryStringValidator(),
}

export function validate(
    input: LoginFormInput,
): Result.Result<LoginFormValidation, LoginData.M> {
    const validationResults = {
        email: validators.email(input.email),
        password: validators.password(input.password),
    }
    if (validationResults.email.type === 'Ok' &&
        validationResults.password.type === 'Ok'
    ) {
        return {
            type: 'Ok',
            value: {
                email: validationResults.email.value,
                password: validationResults.password.value,
            },
        }
    } else {
        return {
            type: 'Err',
            error: {
                email: Result.errorToNullable(validationResults.email),
                password: Result.errorToNullable(validationResults.password),
            },
        }
    }
}

function resetError<Error>(which: Error, current: Error | null): Error | null {
    return which === current ? null : current
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Components/LoginForm/LoginFormReducer.ts